/**
 * usePrefersReducedMotion — reactive `prefers-reduced-motion` media query.
 *
 * Realtime surfaces use this to degrade honestly: counters snap instead of tween,
 * sparks render static, pulses stop. Shared so every consumer inherits one behavior.
 */

const React = (globalThis as any).React;
const { useState, useEffect } = React;

const QUERY = '(prefers-reduced-motion: reduce)';

function readInitial(): boolean {
  try {
    const mm = (globalThis as any).matchMedia;
    return typeof mm === 'function' ? !!mm(QUERY).matches : false;
  } catch {
    return false;
  }
}

export function usePrefersReducedMotion(): boolean {
  // React is provided via globalThis (untyped) — cast instead of using generic args.
  const [reduced, setReduced] = (useState as any)(readInitial) as [boolean, (v: boolean) => void];

  useEffect(() => {
    const mm = (globalThis as any).matchMedia;
    if (typeof mm !== 'function') return;
    const mql = mm(QUERY);
    const onChange = () => setReduced(!!mql.matches);
    onChange();
    // addEventListener is the modern API; addListener is the deprecated fallback.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    if (typeof mql.addListener === 'function') {
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    }
    return undefined;
  }, []);

  return reduced;
}

export default usePrefersReducedMotion;
